import { useState, useCallback, useRef, type DragEvent, type ChangeEvent } from 'react'
import { FileTextIcon, LoaderIcon } from 'lucide-react'

interface ReceiptDropZoneProps {
  onReceiptProcessed: (data: {
    transaction: {
      unique_reference: string
      account_id: number
      date: string
      memo: string
      amount: number
      amount_currency: string
      category_id: number | null
      has_tax: boolean
    }
    receipt_signed_id: string
    categories: { id: number; name: string; kind: string }[]
    preview_url: string
  }) => void
}

export function ReceiptDropZone({ onReceiptProcessed }: ReceiptDropZoneProps) {
  const [dragging, setDragging] = useState(false)
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const processFile = useCallback(
    async (file: File) => {
      setProcessing(true)
      try {
        const formData = new FormData()
        formData.append('receipt', file)

        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const res = await fetch('/receipt_transactions/process_receipt', {
          method: 'POST',
          headers: csrf ? { 'X-CSRF-Token': csrf } : {},
          body: formData,
        })

        if (res.ok) {
          const data = await res.json()
          const previewUrl = URL.createObjectURL(file)
          onReceiptProcessed({ ...data, preview_url: previewUrl })
        }
      } finally {
        setProcessing(false)
      }
    },
    [onReceiptProcessed]
  )

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      setDragging(false)

      const file = e.dataTransfer.files[0]
      if (file) processFile(file)
    },
    [processFile]
  )

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) processFile(file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [processFile]
  )

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={handleFileChange}
      />
      <div
        onClick={() => !processing && fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`w-full mb-3 rounded-lg border-2 border-dashed p-6 text-center text-[13px] text-muted-foreground flex flex-col items-center justify-center cursor-pointer hover:bg-muted/30 transition-colors ${
          dragging ? 'border-primary bg-primary/3' : 'border-border'
        }`}
      >
        {processing ? (
          <>
            <LoaderIcon className="w-6 h-6 text-muted-foreground mb-2 animate-spin" />
            <span>Processing receipt...</span>
          </>
        ) : (
          <>
            <FileTextIcon className="w-8 h-8 text-muted-foreground/60 mb-2" />
            <span>Drop receipt here or click to select</span>
          </>
        )}
      </div>
    </>
  )
}
